import { Component, type OnInit, ChangeDetectionStrategy } from '@angular/core'
import { AbstractControl, UntypedFormControl, Validators } from '@angular/forms'
import { MatDialogRef } from '@angular/material/dialog'
import { Store } from '@ngrx/store'
import * as lodash from 'lodash'
import moment from 'moment'
import {
  AIChatEntityAction,
  AIChatEntityType,
  AIChatItem,
  AIChatMessageItemWithMetadata,
  AIChatRole,
  AIService,
} from '@tokeet-frontend/ai'
import {
  ActionFailed,
  AddExpenseComplete,
  addRentalComplete,
  ConfirmDialogService,
  epochToViewUTC,
  isSomething,
  PromptDialogService,
  Rental,
  SaveForm,
  toExpenseView,
} from '@tokeet-frontend/tv3-platform'
import { AuthService } from '@tv3/services/auth.service'
import { filter, finalize, switchMap } from 'rxjs/operators'
import { CalendarEvent } from '@tv3/store/calendar/calendar.model'
import {
  OpenEditChargeDialog,
  OpenEditDiscountCodeDialog,
  OpenEditExpenseDialog,
  OpenEditExtraDialog,
  OpenEditHoldDialog,
  OpenEditIncidentDialog,
  OpenEditInventoryDialog,
  OpenEditLosDiscountDialog,
  OpenEditTaskDialog,
  OpenGuestOverlay,
  OpenInquiryOverlay,
  OpenInvoiceOverlay,
  OpenRentalOverlay,
} from '@tv3/store/overlay/overlay.actions'
import { AddTaskComplete } from '@tokeet-frontend/tasks'
import { AddIncident, AddIncidentComplete } from '@tokeet-frontend/incident'
import { AddHoldEventComplete } from '@tv3/store/calendar/calendar.actions'
import { AddInvoiceComplete, Invoice } from '@tokeet-frontend/invoices'
import { AddInquiryComplete } from '@tv3/store/inquiry/inquiry.actions'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { Guest } from '@tv3/store/guest/guest.model'
import { AddGuestComplete } from '@tv3/store/guest/guest.actions'
import { AddExtraComplete, toExtraView } from '@tokeet-frontend/extras'
import { AddChargeComplete } from '@tokeet-frontend/charges'
import { DiscountCode } from '@tokeet-frontend/discount-codes'
import { toLosDiscountView } from '@tokeet-frontend/los-discount'
import { AddInventoryComplete, InventoryStatus, inventoryStatuses, InventoryView } from '@tokeet-frontend/inventories'

@Component({
  selector: 'app-ai-chat-overlay',
  templateUrl: './ai-chat-overlay.component.html',
  styleUrls: ['./ai-chat-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AiChatOverlayComponent implements OnInit {
  messageCtrl = new UntypedFormControl('', [Validators.required])
  messages: (AIChatMessageItemWithMetadata | AIChatMessageItemWithMetadata[])[] = []

  get isEmpty() {
    return !this.currentChatId && this.messages?.length === 0
  }

  get userName() {
    return this.auth.user?.firstName
  }

  currentChatId: string | null = null

  roles = AIChatRole
  entityTypes = AIChatEntityType
  actionTypes = AIChatEntityAction
  suggestionQuestions = [
    'Update guest details for [Guest Name]',
    'Create a new booking for [Guest Name] at [Property Name] on [Date]',
    'Generate an invoice for booking [Booking ID]',
    'Mark task as completed',
    'Cancel booking [Booking ID]',
    'Assign maintenance task to [User]',
  ]

  avatarSize = 35

  isLoading = false
  chats: AIChatItem[] = []
  groupedChats: { key: string; value: AIChatItem[] }[] = []
  isHistoryExpanded = false
  readonly INITIAL_HISTORY_LIMIT = 5

  constructor(
    private dialogRef: MatDialogRef<AiChatOverlayComponent>,
    private aiService: AIService,
    private store: Store<any>,
    private auth: AuthService,
    private confirm: ConfirmDialogService,
    private prompt: PromptDialogService
  ) {}
  ngOnInit(): void {
    this.refreshChats()
  }

  refreshChats() {
    this.aiService.getAllChats().subscribe(
      (chats) => {
        this.chats = chats
        this.groupedChats = this.getGroupedChats(chats.slice(0, this.INITIAL_HISTORY_LIMIT))
      },
      () => {}
    )
  }

  close() {
    this.dialogRef.close()
  }

  isViewable(action: AIChatEntityAction) {
    return (
      action === AIChatEntityAction.Created ||
      action === AIChatEntityAction.Updated ||
      action === AIChatEntityAction.Found
    )
  }

  isDeletable(type: AIChatEntityType, action: AIChatEntityAction) {
    return type !== AIChatEntityType.Expense && action !== AIChatEntityAction.Deleted
  }

  @SaveForm()
  onSend(ctrl: AbstractControl) {
    this.onSendMessage(ctrl.value)
  }

  onVoice(data: Blob) {
    this.onTranscript(data)
  }

  onSuggestMessage(message: string) {
    this.prompt
      .open(
        {
          title: $localize`Ask AI?`,
          help: $localize`Please alter the message if necessary.`,
          defaultValue: message,
          placeholder: $localize`Ask AI about this...`,
          buttonText: $localize`Ask AI`,
          buttonIcon: 'cs-icon cs-icon-ai',
        },
        '650px'
      )
      .pipe(filter(isSomething))
      .subscribe((msg) => {
        this.onSendMessage(msg)
      })
  }

  onTranscript(data: Blob) {
    this.isLoading = true
    this.aiService
      .transcript(data)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        (res) => {
          this.messageCtrl.setValue(res.text)
        },
        (error) => this.store.dispatch(ActionFailed({ error }))
      )
  }

  getEntityType(type: AIChatEntityType) {
    switch (type) {
      case AIChatEntityType.Inquiry:
        return 'Booking'
      case AIChatEntityType.Calendar:
        return 'Hold Event'
      default:
        return lodash.startCase(type)
    }
  }

  onViewEntity(type: AIChatEntityType, entity: any) {
    switch (type) {
      case AIChatEntityType.Task:
        this.store.dispatch(AddTaskComplete({ task: entity }))
        this.store.dispatch(OpenEditTaskDialog({ taskId: entity.pkey }))
        break
      case AIChatEntityType.Incident:
        this.store.dispatch(AddIncidentComplete({ incident: entity }))
        this.store.dispatch(OpenEditIncidentDialog({ incidentId: entity.pkey }))
        break
      case AIChatEntityType.Expense:
        this.store.dispatch(AddExpenseComplete({ expense: toExpenseView(entity) }))
        this.store.dispatch(OpenEditExpenseDialog({ expenseId: entity.pkey }))
        break
      case AIChatEntityType.Calendar:
        const calendarEvent = CalendarEvent.deserialize(entity)
        this.store.dispatch(AddHoldEventComplete({ events: [calendarEvent] }))
        this.store.dispatch(OpenEditHoldDialog({ data: { hold: calendarEvent } }))
        break
      case AIChatEntityType.Invoice:
        this.store.dispatch(AddInvoiceComplete({ invoice: Invoice.deserialize(entity) }))
        this.store.dispatch(OpenInvoiceOverlay({ invoiceId: entity.pkey }))
        break
      case AIChatEntityType.Inquiry:
        this.store.dispatch(AddInquiryComplete({ inquiry: Inquiry.deserialize(entity) }))
        this.store.dispatch(OpenInquiryOverlay({ inquiryId: entity.pkey }))
        break
      case AIChatEntityType.Guest:
        this.store.dispatch(AddGuestComplete({ guest: Guest.deserialize(entity) }))
        this.store.dispatch(OpenGuestOverlay({ guestId: entity.pkey }))
        break
      case AIChatEntityType.Rental:
        this.store.dispatch(addRentalComplete({ rental: Rental.deserialize(entity) }))
        this.store.dispatch(OpenRentalOverlay({ rentalId: entity.pkey }))
        break
      case AIChatEntityType.Inventory:
        const inventory = this.toInventoryView(entity)
        this.store.dispatch(AddInventoryComplete({ inventory }))
        this.store.dispatch(OpenEditInventoryDialog({ inventory }))
        break
      case AIChatEntityType.Extra:
        this.store.dispatch(AddExtraComplete({ extra: toExtraView(entity) }))
        this.store.dispatch(OpenEditExtraDialog({ extra: entity }))
        break
      case AIChatEntityType.Charge:
        this.store.dispatch(AddChargeComplete({ charge: entity }))
        this.store.dispatch(OpenEditChargeDialog({ charge: entity }))
        break
      case 'discount' as any:
      case AIChatEntityType.LosDiscount:
        this.store.dispatch(OpenEditLosDiscountDialog({ losDiscount: toLosDiscountView(entity) }))
        break
      case AIChatEntityType.DiscountCode:
        this.store.dispatch(OpenEditDiscountCodeDialog({ discountCode: entity as DiscountCode }))
        break
    }
  }

  onDeleteEntity(type: AIChatEntityType, entity: any) {
    let message: string
    switch (type) {
      case AIChatEntityType.Task:
        message = `Delete task "${entity.name}" (${entity.pkey})`
        break
      case AIChatEntityType.Incident:
        message = `Delete incident "${entity.title}" (${entity.pkey})`
        break
      case AIChatEntityType.Expense:
        message = `Delete expense "${entity.category}, ${entity.description}" (${entity.pkey})`
        break
      case AIChatEntityType.Calendar:
        message = `Delete hold event "${entity.title}" (${entity.pkey})`
        break
      case AIChatEntityType.Invoice:
        message = `Delete invoice "${entity.invoice_num}" (${entity.pkey})`
        break
      case AIChatEntityType.Inquiry:
        message = `Delete inquiry for "${entity.guest_details?.name}" (${entity.pkey})`
        break
      case AIChatEntityType.Guest:
        message = `Delete guest "${entity.name}" (${entity.pkey})`
        break
      case AIChatEntityType.Rental:
        message = `Delete rental "${entity.name}" (${entity.pkey})`
        break
      case AIChatEntityType.Inventory:
        message = `Delete inventory "${entity.title}" (${entity.pkey})`
        break
      case AIChatEntityType.Extra:
        message = `Delete extra "${entity.category}, ${entity.description}" (${entity.pkey})`
        break
      case AIChatEntityType.Charge:
        message = `Delete charge "${entity.description}" (${entity.pkey})`
        break
      case 'discount' as any:
      case AIChatEntityType.LosDiscount:
        message = `Delete LOS discount "${entity.name}" (${entity.id || entity.pkey})`
        break
      case AIChatEntityType.DiscountCode:
        message = `Delete discount code "${entity.code || entity.name}" (${entity.id || entity.pkey})`
        break
    }
    if (message) {
      this.confirm
        .confirm({
          title: $localize`Are you sure?`,
          body: $localize`Are you sure you want to delete this entity?`,
        })
        .subscribe(() => {
          this.onSendMessage(message)
        })
    }
  }

  onSendMessage(message: string) {
    const messages = [...this.messages, { role: AIChatRole.User, content: message }]
    this.messages = messages
    this.isLoading = true
    this.messageCtrl.disable()
    this.messageCtrl.reset()
    this.aiService
      .chat(lodash.flatten(messages), this.currentChatId, 'html')
      .pipe(
        finalize(() => {
          this.isLoading = false
          this.messageCtrl.enable()
        })
      )
      .subscribe(
        (res) => {
          this.currentChatId = res.id
          this.messages = this.groupMessages([...lodash.flatten(this.messages), ...res.replies])
        },
        (error) => this.store.dispatch(ActionFailed({ error }))
      )
  }

  groupMessages(messages: AIChatMessageItemWithMetadata[]) {
    let system: AIChatMessageItemWithMetadata[] = []
    const groups = lodash.reduce(
      messages,
      (acc, item) => {
        if (item.role === AIChatRole.System) {
          system.push(item)
        } else if (!!system.length) {
          acc.push(system)
          acc.push(item)
          system = []
        } else {
          acc.push(item)
        }
        return acc
      },
      []
    )

    if (!!system.length) {
      groups.push(system)
    }

    return groups
  }

  onNewChat() {
    this.messages = []
    this.currentChatId = null
    this.refreshChats()
  }

  onDeleteChat(chatId: string) {
    this.confirm
      .confirm({
        title: $localize`Are you sure?`,
        body: $localize`Are you sure you want to delete this chat?`,
      })
      .pipe(switchMap(() => this.aiService.deleteChat(chatId)))
      .subscribe(
        () => {
          this.chats = this.chats.filter((chat) => chat.id !== chatId)
          this.onNewChat()
        },
        (error) => this.store.dispatch(ActionFailed({ error }))
      )
  }

  onViewChat(chatId: string) {
    this.currentChatId = chatId
    this.isLoading = true
    this.aiService
      .getChatHistory(chatId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        (res) => {
          this.messages = this.groupMessages(res.replies)
        },
        (error) => this.store.dispatch(ActionFailed({ error }))
      )
  }

  onExpandHistory() {
    this.isHistoryExpanded = true
    this.groupedChats = this.getGroupedChats(this.chats)
  }

  getGroupedChats(items: AIChatItem[]) {
    const now = moment()
    const groups = lodash.groupBy(items, (chat) => {
      const updatedAt = moment.unix(chat.updatedAt)
      const diff = now.diff(updatedAt, 'days')

      if (diff === 0) {
        return 'Today'
      } else if (diff === 1) {
        return 'Yesterday'
      } else if (diff < 7) {
        return 'This Week'
      } else if (diff < 30) {
        return 'This Month'
      } else {
        return 'Older'
      }
    })

    // Sort chats within each group by updatedAt in descending order
    Object.keys(groups).forEach((group) => {
      groups[group] = lodash.sortBy(groups[group], 'updatedAt').reverse()
    })

    // Sort groups by recency
    const groupOrder = ['Today', 'Yesterday', 'This Week', 'This Month', 'Older']
    const sortedGroups: { key: string; value: AIChatItem[] }[] = []
    groupOrder.map((group) => {
      if (groups[group]) {
        sortedGroups.push({ key: group, value: groups[group] })
      }
    })

    return sortedGroups
  }

  private toInventoryView(entity: any): InventoryView {
    const status = +(entity.status || 0) as InventoryStatus
    return {
      ...entity,
      status,
      archived: +(entity.archived || 0),
      createdView: entity.createdView || epochToViewUTC(entity.created),
      rentalView: entity.rentalView || '',
      statusView: entity.statusView || inventoryStatuses.find((item) => item.value === status)?.label,
    } as InventoryView
  }
}
